import React, { useState, useRef, useEffect } from 'react';

// Shared building blocks for collection grids — used by both the canvas
// CollectionModal and the standalone Tables view, so field rows, cells and
// pickers behave identically everywhere.

export const FIELD_TYPES = [
  { value: 'text',     label: 'Text',     icon: '📝' },
  { value: 'number',   label: 'Number',   icon: '🔢' },
  { value: 'select',   label: 'Select',   icon: '🔽' },
  { value: 'date',     label: 'Date',     icon: '📅' },
  { value: 'relation', label: 'Relation', icon: '🔗' },
];

export const STATUS_COLORS = {
  Open: '#3b82f6', 'In Progress': '#f59e0b', Resolved: '#10b981',
  Done: '#10b981', Escalated: '#f43f5e', Backlog: '#94a3b8', Review: '#8b5cf6',
};

export function typeIcon(type) {
  return FIELD_TYPES.find(t => t.value === type)?.icon || '📝';
}

// ── Field config row ─────────────────────────────────────────────────────────
export function FieldRow({ field, allCollections, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [newOpt, setNewOpt] = useState('');

  const addOption = () => {
    const v = newOpt.trim();
    if (!v) return;
    const opts = [...(field.options || []), v];
    onUpdate({ options: opts });
    setNewOpt('');
  };

  const removeOption = (i) => {
    onUpdate({ options: (field.options || []).filter((_, j) => j !== i) });
  };

  // Edit an existing option in place (rename) rather than delete + recreate.
  const updateOption = (i, val) => {
    onUpdate({ options: (field.options || []).map((o, j) => (j === i ? val : o)) });
  };

  const needsExpand = field.type === 'select' || field.type === 'relation';

  return (
    <div className={`cm-field-row ${expanded ? 'expanded' : ''}`}>
      <div className="cm-field-main">
        <span className="cm-field-type-icon" title={field.type}>{typeIcon(field.type)}</span>
        <input
          className="cm-field-name-input"
          value={field.name}
          onChange={e => onUpdate({ name: e.target.value })}
          placeholder="Field name"
        />
        <select
          className="cm-field-type-sel"
          value={field.type}
          onChange={e => {
            const t = e.target.value;
            const update = { type: t };
            if (t === 'select') update.options = field.options || [];
            if (t === 'relation') update.targetCollectionId = field.targetCollectionId || null;
            onUpdate(update);
          }}
        >
          {FIELD_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
          ))}
        </select>
        {needsExpand && (
          <button className="cm-field-expand-btn" onClick={() => setExpanded(e => !e)} title="Configure">
            {expanded ? '▲' : '▼'}
          </button>
        )}
        <button className="cm-field-del-btn" onClick={onDelete} title="Delete field">✕</button>
      </div>

      {expanded && field.type === 'select' && (
        <div className="cm-field-opts">
          <div className="cm-opts-label">Dropdown options:</div>
          <div className="cm-opts-chips">
            {(field.options || []).length === 0 && (
              <span className="cm-opts-empty">No options yet</span>
            )}
            {(field.options || []).map((opt, i) => (
              <span key={i} className="cm-opt-chip">
                <input
                  className="cm-opt-edit"
                  value={opt}
                  size={Math.max(opt.length, 2)}
                  onChange={e => updateOption(i, e.target.value)}
                  aria-label={`Edit option ${i + 1}`}
                />
                <button onClick={() => removeOption(i)} title="Remove option">×</button>
              </span>
            ))}
          </div>
          <div className="cm-opt-add-row">
            <input
              className="cm-opt-input"
              placeholder="New option…"
              value={newOpt}
              onChange={e => setNewOpt(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addOption(); }}
            />
            <button className="cm-opt-add-btn" onClick={addOption} disabled={!newOpt.trim()}>+ Add</button>
          </div>
        </div>
      )}

      {expanded && field.type === 'relation' && (
        <div className="cm-field-relation">
          <div className="cm-opts-label">Links to collection:</div>
          {allCollections.length === 0 ? (
            <span className="cm-opts-empty">No other collections yet</span>
          ) : (
            <select
              className="cm-relation-target"
              value={field.targetCollectionId || ''}
              onChange={e => onUpdate({ targetCollectionId: e.target.value })}
            >
              <option value="">— choose collection —</option>
              {allCollections.map(c => (
                <option key={c.id} value={c.id}>{c.data.label || c.id}</option>
              ))}
            </select>
          )}
          {field.targetCollectionId && (
            <div className="cm-relation-hint">
              Records in this collection will link to individual records in{' '}
              <strong>{allCollections.find(c => c.id === field.targetCollectionId)?.data.label || '…'}</strong>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Single record cell ───────────────────────────────────────────────────────
export function RecordCell({ field, value, allCollections, recordsByCol, onChange }) {
  if (field.type === 'select') {
    return (
      <select className="cm-cell-sel" value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'relation') {
    const target = allCollections.find(c => c.id === field.targetCollectionId);
    const tRecords = recordsByCol[field.targetCollectionId] || [];
    const tTitleF  = target?.data?.fields?.find(f => f.type === 'text') || target?.data?.fields?.[0];
    return (
      <select className="cm-cell-sel" value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        {tRecords.map(r => {
          const display = tTitleF ? (r.values?.[tTitleF.id] || r.id) : r.id;
          return <option key={r.id} value={r.id}>{display}</option>;
        })}
      </select>
    );
  }
  if (field.type === 'date') {
    return <input className="cm-cell-input" type="date" value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  if (field.type === 'number') {
    return <input className="cm-cell-input cm-cell-num" type="number" value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
  return <input className="cm-cell-input" type="text" value={value || ''} onChange={e => onChange(e.target.value)} />;
}

// ── Right-click context menu ─────────────────────────────────────────────────
export function RecordCtxMenu({ x, y, record, fields, allCollections, onLink, onDelete, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const relationFields = fields.filter(f => f.type === 'relation' && f.targetCollectionId);

  return (
    <div ref={ref} className="cm-ctx-menu" style={{ top: y, left: x }}>
      {relationFields.length > 0 && (
        <>
          <div className="cm-ctx-section">Link to collection</div>
          {relationFields.map(f => {
            const target = allCollections.find(c => c.id === f.targetCollectionId);
            return (
              <button key={f.id} className="cm-ctx-item" onClick={() => { onLink(f); onClose(); }}>
                🔗 {f.name} → <em>{target?.data.label || '…'}</em>
              </button>
            );
          })}
          <div className="cm-ctx-divider" />
        </>
      )}
      {relationFields.length === 0 && (
        <div className="cm-ctx-hint">
          Add a <strong>Relation</strong> field in the Fields tab to enable cross-collection linking.
        </div>
      )}
      <button className="cm-ctx-item cm-ctx-danger" onClick={() => { onDelete(record.id); onClose(); }}>
        🗑️ Delete record
      </button>
    </div>
  );
}

// ── Link picker overlay ───────────────────────────────────────────────────────
export function LinkPicker({ field, allCollections, recordsByCol, currentValue, onPick, onClose }) {
  const target = allCollections.find(c => c.id === field.targetCollectionId);
  const tRecords = recordsByCol[field.targetCollectionId] || [];
  const tFields  = target?.data?.fields  || [];
  const titleF   = tFields.find(f => f.type === 'text') || tFields[0];

  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  return (
    <div className="cm-link-backdrop" onClick={onClose}>
      <div ref={ref} className="cm-link-picker" onClick={e => e.stopPropagation()}>
        <div className="cm-link-hdr">
          <span>🔗 Link to <strong>{target?.data.label || 'collection'}</strong></span>
          <button className="cm-close-btn" aria-label="Close dialog" title="Close" onClick={onClose}>✕</button>
        </div>
        <div className="cm-link-field-label">Linking field: <strong>{field.name}</strong></div>
        {tRecords.length === 0 ? (
          <div className="cm-link-empty">That collection has no records yet. Add some first.</div>
        ) : (
          <div className="cm-link-list">
            <button className="cm-link-row cm-link-clear" onClick={() => { onPick(null); onClose(); }}>
              — Clear link
            </button>
            {tRecords.map(r => {
              const display = titleF ? (r.values?.[titleF.id] || r.id) : r.id;
              const isActive = currentValue === r.id;
              return (
                <button
                  key={r.id}
                  className={`cm-link-row ${isActive ? 'active' : ''}`}
                  onClick={() => { onPick(r.id); onClose(); }}
                >
                  {isActive && <span className="cm-link-check">✓</span>}
                  {display}
                  <span className="cm-link-id">#{r.id.slice(-4)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
